type GpsPoint = {
  lat: number;
  lon: number;
  timestamp: number;
  speed?: number;
  // Carried through the filters so callers can read the heading of the point
  // that survived normalization.
  angle?: number;
};

/**
 * Strategy 1: Distance Pre-Filter (Anti-Jitter)
 * Filters out points moved less than minDistanceMeters.
 * Softened: 2 meters (was 3). Removes only micro-jitter.
 */
function filterStationaryPoints(points: GpsPoint[], minDistanceMeters: number = 2): GpsPoint[] {
  if (points.length < 2) return points;

  const filtered: GpsPoint[] = [points[0]];
  let lastPoint = points[0];

  for (let i = 1; i < points.length; i++) {
    const current = points[i];
    const dist = getDistanceFromLatLonInM(lastPoint.lat, lastPoint.lon, current.lat, current.lon);

    // Keep point if it moved enough
    if (dist >= minDistanceMeters) {
      filtered.push(current);
      lastPoint = current;
    }
  }

  return filtered;
}

/**
 * Strategy 2: Directional Outlier Detection
 * Removes points that deviate significantly from the current vector of movement.
 * Softened: 80 degrees (was 60). Allows sharper turns.
 */
function filterOutliersByDirection(
  points: GpsPoint[],
  deviationThresholdDegrees: number = 80,
): GpsPoint[] {
  if (points.length < 3) return points;

  const filtered: GpsPoint[] = [points[0], points[1]];

  for (let i = 2; i < points.length; i++) {
    const p1 = filtered[filtered.length - 2];
    const p2 = filtered[filtered.length - 1];
    const p3 = points[i];

    const bearing1 = getBearing(p1.lat, p1.lon, p2.lat, p2.lon);
    const bearing2 = getBearing(p2.lat, p2.lon, p3.lat, p3.lon);

    let diff = Math.abs(bearing1 - bearing2);
    if (diff > 180) diff = 360 - diff;

    // If angle change is reasonable, keep the point
    if (diff <= deviationThresholdDegrees) {
      filtered.push(p3);
    }
  }

  return filtered;
}

/**
 * Master Normalization Function (Hard Changes Only)
 */
export function normalizeGpsData(points: GpsPoint[]): GpsPoint[] {
  let output = points;

  // 1. Remove duplicate/stationary points (0.5m)
  // Necessary to avoid division by zero in angle calculations
  output = filterStationaryPoints(output, 1);

  // 2. Remove only extreme outliers (150 degrees)
  // Only removes points that create a sharp "spike" (back-and-forth)
  output = filterOutliersByDirection(output, 150);

  // 3. NO Kalman Filter (Smoothing disabled)
  // We keep the raw track geometry, only removing glitches

  return output;
}

// Helpers
function getDistanceFromLatLonInM(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d * 1000;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

function rad2deg(rad: number) {
  return rad * (180 / Math.PI);
}

function getBearing(startLat: number, startLon: number, endLat: number, endLon: number) {
  const startLatRad = deg2rad(startLat);
  const startLonRad = deg2rad(startLon);
  const endLatRad = deg2rad(endLat);
  const endLonRad = deg2rad(endLon);

  const y = Math.sin(endLonRad - startLonRad) * Math.cos(endLatRad);
  const x =
    Math.cos(startLatRad) * Math.sin(endLatRad) -
    Math.sin(startLatRad) * Math.cos(endLatRad) * Math.cos(endLonRad - startLonRad);

  let bearing = Math.atan2(y, x);
  bearing = rad2deg(bearing);

  return (bearing + 360) % 360;
}
